import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  Camera, 
  Save, 
  Trash2,
  Shield
} from 'lucide-react';
import MobileHeader from '../components/MobileHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_LABELS = {
  gerencia: 'Gerencia',
  admin: 'Administrador',
  asesor: 'Asesor',
  diseñador: 'Diseñador'
};

const UserProfile = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    profile_photo: ''
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        profile_photo: user.profile_photo || ''
      });
      setPhotoPreview(user.profile_photo || null);
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formato no válido. Use JPG, PNG o WEBP');
      return;
    }

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }

    // Convertir a base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setPhotoPreview(base64);
      setFormData(prev => ({ ...prev, profile_photo: base64 }));
      setHasChanges(true);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setPhotoPreview(null);
    setFormData(prev => ({ ...prev, profile_photo: '' }));
    setHasChanges(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone) return true; // Opcional
    // Permite formatos internacionales: +57 300 123 4567, (300) 123-4567, etc.
    const re = /^[\d\s\+\-\(\)]{7,20}$/;
    return re.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.username.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    if (!validateEmail(formData.email)) {
      toast.error('Formato de correo no válido');
      return;
    }
    
    if (!validatePhone(formData.phone)) {
      toast.error('Formato de teléfono no válido');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(`${API_URL}/auth/profile`, {
        username: formData.username,
        email: formData.email,
        phone: formData.phone || null,
        profile_photo: formData.profile_photo || null
      });
      
      // Actualizar el contexto de usuario
      if (response.data) {
        // Forzar recarga de datos del usuario
        window.location.reload();
      }
      
      toast.success('Perfil actualizado correctamente');
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Mobile Header */}
      <MobileHeader />
      
      {/* Desktop Header Premium Negro */}
      <header className="bg-black sticky top-0 z-10 hidden md:block">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/60 hover:text-white transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Volver</span>
              </button>
              <div className="w-px h-6 bg-white/20" />
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-white/70" />
                <h1 className="font-serif text-lg text-white">Mi Perfil</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Save button floating */}
        {hasChanges && (
          <div className="mb-6">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 bg-black text-white px-4 py-2 text-[10px] uppercase tracking-wider font-medium hover:bg-zinc-800 transition-all disabled:opacity-50"
              data-testid="save-profile-btn"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Foto de Perfil */}
          <div className="bg-white border border-border p-6">
            <h2 className="text-sm font-bold uppercase tracking-wider mb-4 text-muted-foreground">
              Foto de Perfil
            </h2>
            
            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* Preview de foto */}
              <div className="relative">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-zinc-100 border-2 border-border flex items-center justify-center">
                  {photoPreview ? (
                    <img 
                      src={photoPreview} 
                      alt="Foto de perfil" 
                      className="w-full h-full object-cover"
                      data-testid="profile-photo-preview"
                    />
                  ) : (
                    <User className="w-16 h-16 text-zinc-300" />
                  )}
                </div>
                
                {/* Botón de cámara */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-zinc-800 transition-colors"
                  data-testid="change-photo-btn"
                >
                  <Camera className="w-5 h-5" />
                </button>
              </div>
              
              {/* Acciones de foto */}
              <div className="flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoSelect}
                  className="hidden"
                  data-testid="photo-input"
                />
                
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 border border-border px-4 py-2 uppercase tracking-widest text-xs font-medium hover:bg-zinc-50 transition-colors"
                  data-testid="upload-photo-btn"
                >
                  <Camera className="w-4 h-4" />
                  Subir Foto
                </button>
                
                {photoPreview && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="flex items-center gap-2 border border-destructive text-destructive px-4 py-2 uppercase tracking-widest text-xs font-medium hover:bg-destructive hover:text-white transition-colors"
                    data-testid="remove-photo-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    Eliminar
                  </button>
                )}
                
                <p className="text-xs text-muted-foreground mt-2">
                  JPG, PNG o WEBP. Máximo 5MB.
                </p>
              </div>
            </div>
          </div>

          {/* Información Personal */}
          <div className="bg-white border border-border p-6">
            <h2 className="text-sm font-bold uppercase tracking-wider mb-4 text-muted-foreground">
              Información Personal
            </h2>
            
            <div className="space-y-4">
              {/* Nombre */}
              <div>
                <label className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold mb-2">
                  <User className="w-4 h-4" />
                  Nombre
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className="w-full border border-border px-4 py-3 focus:outline-none focus:ring-1 focus:ring-black"
                  placeholder="Tu nombre"
                  required
                  data-testid="input-username"
                />
              </div>
              
              {/* Correo */}
              <div>
                <label className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold mb-2">
                  <Mail className="w-4 h-4" />
                  Correo Electrónico
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full border border-border px-4 py-3 focus:outline-none focus:ring-1 focus:ring-black"
                  placeholder="tu@email.com"
                  required
                  data-testid="input-email"
                />
              </div>
              
              {/* Teléfono */}
              <div>
                <label className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold mb-2">
                  <Phone className="w-4 h-4" />
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full border border-border px-4 py-3 focus:outline-none focus:ring-1 focus:ring-black"
                  placeholder="+57 300 123 4567"
                  data-testid="input-phone"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Formato internacional permitido
                </p>
              </div>
            </div>
          </div>

          {/* Información de Cuenta (Solo lectura) */}
          <div className="bg-zinc-50 border border-border p-6">
            <h2 className="text-sm font-bold uppercase tracking-wider mb-4 text-muted-foreground">
              Información de Cuenta
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold mb-2 text-muted-foreground">
                  <Shield className="w-4 h-4" />
                  Rol
                </label>
                <div className="px-4 py-3 bg-white border border-border text-muted-foreground">
                  {ROLE_LABELS[user.role] || user.role}
                </div>
              </div>
              
              <div>
                <label className="text-xs uppercase tracking-wider font-bold mb-2 text-muted-foreground block">
                  Estado
                </label>
                <div className="px-4 py-3 bg-white border border-border">
                  <span className={user.is_active ? 'text-green-600' : 'text-red-600'}>
                    {user.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground mt-4">
              Para cambiar tu rol o estado, contacta al administrador del sistema.
            </p>
          </div>

          {/* Botón guardar (visible siempre en móvil) */}
          <div className="sm:hidden">
            <button
              type="submit"
              disabled={loading || !hasChanges}
              className="w-full flex items-center justify-center gap-2 bg-black text-white px-4 py-4 rounded-none uppercase tracking-widest text-xs font-medium hover:bg-zinc-800 transition-all disabled:opacity-50"
              data-testid="save-profile-btn-mobile"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default UserProfile;
